import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer'
import {
  DeclarationSkuCountDto,
  PrintDeclarationSkuDto,
  ReturnDeclarationSku,
  ReturnDeclarationSkuDTO,
  ReturnDeclarationSkuListRequestFilter,
  ReturnDeclarationSkuListRequestSorting,
  ReturnDeclarationSkuState,
} from './types'
import { Progress } from 'modules/types'
import { BasicEntityState } from 'modules/constants'
import { arrToDict, getIds } from 'modules/utils/helpers'
import { AddError, FetchError, RemoveError, UpdateError } from '../types'

const initialState: ReturnDeclarationSkuState = {
  ...BasicEntityState,
  printProgress: Progress.IDLE,
  printError: null,
  declarationCountFetchProgress: Progress.IDLE,
  declarationCountFetchError: null,
  declarationCount: null,

  listUpdateProgress: Progress.IDLE,
  listUpdateError: null,

  totalFinalQty: 0,
  totalQty: 0,
  totalDeliveredQty: 0,
  totalStandardQty: 0,
}

class ReturnDeclarationSkuReducer extends ImmerReducer<ReturnDeclarationSkuState> {
  listRequested(params: {
    filter?: ReturnDeclarationSkuListRequestFilter
    sorting?: ReturnDeclarationSkuListRequestSorting
    page?: number
  }) {
    this.draftState.listFetchProgress = Progress.WORK
    this.draftState.listFetchError = null
    this.draftState.filter = params.filter || this.draftState.filter
    this.draftState.sorting = params.sorting || this.draftState.sorting
    this.draftState.page = typeof params.page === 'undefined' ? this.draftState.page : params.page
  }
  listRequestSucceed(
    list: ReturnDeclarationSku[],
    total: number,
    totalQty: number,
    totalFinalQty: number,
    totalDeliveredQty: number,
    totalStandardQty: number,

    page: number,
  ) {
    this.draftState.listFetchProgress = Progress.SUCCESS
    this.draftState.items = arrToDict(list)
    this.draftState.meta = arrToDict(
      list.map((item) => ({
        id: item.id,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
      })),
    )
    this.draftState.ids = getIds(list)
    this.draftState.total = total
    this.draftState.page = page
    this.draftState.totalQty = totalQty
    this.draftState.totalFinalQty = totalFinalQty
    this.draftState.totalDeliveredQty = totalDeliveredQty
    this.draftState.totalStandardQty = totalStandardQty
  }
  listRequestFailed(error: FetchError) {
    this.draftState.listFetchProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }
  listUpdateRequested() {
    this.draftState.listUpdateProgress = Progress.WORK
    this.draftState.listUpdateError = null
  }
  listUpdateRequestSucceed(list: ReturnDeclarationSku[], total: number) {
    this.draftState.listUpdateProgress = Progress.SUCCESS
    this.draftState.items = arrToDict(list)
    this.draftState.meta = arrToDict(
      list.map((item) => ({
        id: item.id,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
      })),
    )
    this.draftState.ids = getIds(list)
    this.draftState.total = total
  }
  listUpdateRequestFailed(error: UpdateError) {
    this.draftState.listUpdateProgress = Progress.ERROR
    this.draftState.listUpdateError = error
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  itemRequested(id: string) {
    this.draftState.itemFetchProgress = Progress.WORK

    const meta = {
      id,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }

    this.draftState.meta[id] = {
      ...meta,
      ...this.draftState.meta[id],
      fetchProgress: Progress.WORK,
      fetchError: null,
    }
  }
  itemRequestSucceed(item: ReturnDeclarationSku) {
    this.draftState.itemFetchProgress = Progress.SUCCESS
    this.draftState.meta[item.id].fetchProgress = Progress.SUCCESS
    this.draftState.meta[item.id].fetchError = null
    this.draftState.items[item.id] = item
  }
  itemRequestFailed(id: string, error: FetchError) {
    this.draftState.itemFetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchError = error
  }

  addRequested(_dto: ReturnDeclarationSkuDTO, _duplicate: boolean) {
    this.draftState.addProgress = Progress.WORK
    this.draftState.addError = null
  }
  addSucceed(item: ReturnDeclarationSku) {
    this.draftState.addProgress = Progress.SUCCESS
    this.draftState.items[item.id] = item
    this.draftState.meta[item.id] = {
      id: item.id,
      fetchProgress: Progress.SUCCESS,
      fetchError: null,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }
  }
  addFailed(error: AddError) {
    this.draftState.addProgress = Progress.ERROR
    this.draftState.addError = error
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateRequested(id: string, item: Partial<ReturnDeclarationSkuDTO>, duplicate: boolean) {
    this.draftState.updateProgress = Progress.WORK
    this.draftState.meta[id].updateProgress = Progress.WORK
    this.draftState.meta[id].updateError = null
  }
  updateSucceed(item: ReturnDeclarationSku) {
    this.draftState.items[item.id] = item
    this.draftState.updateProgress = Progress.SUCCESS
    this.draftState.meta[item.id].updateProgress = Progress.SUCCESS
    this.draftState.meta[item.id].updateError = null
  }
  updateFailed(id: string, error: UpdateError) {
    this.draftState.updateProgress = Progress.ERROR
    this.draftState.meta[id].updateProgress = Progress.ERROR
    this.draftState.meta[id].updateError = error
  }

  filterUpdated(filter: ReturnDeclarationSkuListRequestFilter) {
    this.draftState.filter = filter
    this.draftState.listFetchProgress = Progress.WORK
  }

  filtersUpdatedWithPersistentStorage(filter: ReturnDeclarationSkuListRequestFilter) {
    this.draftState.filter = { ...this.draftState.filter, ...filter }
  }

  sortingUpdated(sorting: ReturnDeclarationSkuListRequestSorting) {
    this.draftState.sorting = sorting
    this.draftState.listFetchProgress = Progress.WORK
  }

  filterHasBeenReset() {
    this.draftState.filter = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingHasBeenReset() {
    this.draftState.sorting = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  listRequestedNext(page: number) {
    this.draftState.page = page
    this.draftState.listFetchNextProgress = Progress.WORK
    this.draftState.listFetchError = null
  }

  listRequestNextSucceed(list: ReturnDeclarationSku[], total: number) {
    this.draftState.listFetchNextProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.items = { ...this.draftState.items, ...arrToDict(list) }
    this.draftState.ids = [...this.draftState.ids, ...getIds(list)]
  }

  listRequestNextFailed(error: FetchError) {
    this.draftState.listFetchNextProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeRequested(id: string) {
    this.draftState.removeProgress = Progress.WORK
    this.draftState.meta[id].removeProgress = Progress.WORK
    this.draftState.meta[id].removeError = null
  }
  removeSucceed(id: string) {
    this.draftState.removeProgress = Progress.SUCCESS
    delete this.draftState.items[id]
    delete this.draftState.meta[id]
    const i = this.draftState.ids.findIndex((item) => item === id)
    if (-1 !== i) {
      const ids = this.draftState.ids
      this.draftState.ids = [...ids.slice(0, i), ...ids.slice(i + 1)]
    }
  }
  removeFailed(id: string, error: RemoveError) {
    this.draftState.removeProgress = Progress.ERROR
    this.draftState.meta[id].removeProgress = Progress.ERROR
    this.draftState.meta[id].removeError = error
  }

  resetRemoveProgress() {
    this.draftState.removeProgress = Progress.IDLE
  }

  printRequested(_dto: PrintDeclarationSkuDto) {
    this.draftState.printProgress = Progress.WORK
    this.draftState.printError = null
  }

  printSucceed() {
    this.draftState.printProgress = Progress.SUCCESS
    this.draftState.printError = null
  }
  printFailed(error: any) {
    this.draftState.printProgress = Progress.ERROR
    this.draftState.printError = error
  }
  countRequested(_dto: DeclarationSkuCountDto) {
    this.draftState.declarationCountFetchProgress = Progress.WORK
    this.draftState.declarationCountFetchError = null
  }
  countRequestedSucceed(count: number) {
    this.draftState.declarationCountFetchProgress = Progress.SUCCESS
    this.draftState.declarationCountFetchError = null
    this.draftState.declarationCount = count
  }
  countRequestedFailed(error: any) {
    this.draftState.declarationCountFetchProgress = Progress.ERROR
    this.draftState.declarationCountFetchError = error
  }
  cleanDeclarationCounter() {
    this.draftState.declarationCountFetchProgress = Progress.IDLE
    this.draftState.declarationCount = null
  }
}

export const ReturnDeclarationSkuActions = createActionCreators(ReturnDeclarationSkuReducer)
export default ReturnDeclarationSkuActions
export const reducer = createReducerFunction(ReturnDeclarationSkuReducer, initialState)
